import React, { useState } from "react";
import SubPageLayout from "../../../components/shared/SubPageLayout";

const ComplianceCenter = () => {
  const breadcrumbItems = [
    { label: "Administrative", link: "/administrative" },
    { label: "Compliance Center", link: "/administrative/compliance-center" },
  ];

  // State for regulatory checklist
  const [checklistItems, setChecklistItems] = useState([
    {
      id: 1,
      task: "SEC Form ADV Annual Update",
      status: "pending",
      category: "Regulatory Filing",
      dueDate: "2025-03-31",
      priority: "high",
      progress: 25,
    },
    {
      id: 2,
      task: "Anti-Money Laundering Review",
      status: "in_progress",
      category: "Internal Audit",
      dueDate: "2025-04-15",
      priority: "medium",
      progress: 60,
    },
    {
      id: 3,
      task: "Privacy Policy Update",
      status: "completed",
      category: "Documentation",
      dueDate: "2025-03-15",
      priority: "low",
      progress: 100,
    },
  ]);

  // State for document repository
  const [documents, setDocuments] = useState([
    {
      id: 1,
      name: "Form ADV Part 2A",
      category: "Regulatory",
      type: "PDF",
      uploadDate: "2025-02-15",
      uploader: "John Smith",
      tags: ["SEC", "ADV", "Disclosure"],
    },
    {
      id: 2,
      name: "Code of Ethics",
      category: "Policies",
      type: "DOCX",
      uploadDate: "2025-01-20",
      uploader: "Sarah Johnson",
      tags: ["Ethics", "Policy", "Internal"],
    },
  ]);

  // State for audit logs
  const [auditLogs, setAuditLogs] = useState([
    {
      id: 1,
      timestamp: "2025-03-01T10:30:00",
      user: "John Smith",
      action: "Document Upload",
      details: "Uploaded updated Form ADV",
    },
    {
      id: 2,
      timestamp: "2025-03-01T09:15:00",
      user: "Sarah Johnson",
      action: "Task Completion",
      details: "Completed Privacy Policy Update",
    },
  ]);

  // State for tasks
  const [tasks, setTasks] = useState([
    {
      id: 1,
      name: "Review Trading Compliance",
      assignee: "Mike Wilson",
      dueDate: "2025-03-20",
      priority: "high",
      status: "pending",
    },
    {
      id: 2,
      name: "Update Client Agreements",
      assignee: "Sarah Johnson",
      dueDate: "2025-04-01",
      priority: "medium",
      status: "in_progress",
    },
  ]);

  // State for alerts
  const [alerts, setAlerts] = useState([
    {
      id: 1,
      type: "warning",
      message: "SEC Form ADV due in 30 days",
      timestamp: "2025-03-01T08:00:00",
      isRead: false,
    },
    {
      id: 2,
      type: "info",
      message: "New compliance training available",
      timestamp: "2025-03-01T07:30:00",
      isRead: true,
    },
  ]);

  // State for active tab
  const [activeTab, setActiveTab] = useState("dashboard");

  // Utility function for formatting dates
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Function to get status color
  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-green-100 text-green-800";
      case "in_progress":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800 dark:text-gray-200";
    }
  };

  // Function to get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800 dark:text-gray-200";
    }
  };

  // Render dashboard widgets
  const renderDashboardWidgets = () => (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Compliance Status
        </h3>
        <div className="flex items-center justify-between">
          <div className="text-3xl font-bold text-green-600">85%</div>
          <div className="text-sm text-gray-500 dark:text-gray-500">Overall Compliance Score</div>
        </div>
      </div>
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Upcoming Deadlines
        </h3>
        <div className="space-y-2">
          {tasks.slice(0, 2).map((task) => (
            <div key={task.id} className="flex items-center justify-between">
              <span className="text-sm text-gray-900 dark:text-gray-100">{task.name}</span>
              <span className="text-sm text-gray-500 dark:text-gray-500">
                {formatDate(task.dueDate)}
              </span>
            </div>
          ))}
        </div>
      </div>
      <div className="bg-surface-default dark:bg-surface-default p-6 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">
          Recent Activities
        </h3>
        <div className="space-y-2">
          {auditLogs.slice(0, 2).map((log) => (
            <div key={log.id} className="text-sm text-gray-500 dark:text-gray-500">
              {log.details}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render regulatory checklist
  const renderRegulatoryChecklist = () => (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Regulatory Checklist
        </h2>
        <button className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90">
          Add Task
        </button>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {checklistItems.map((item) => (
            <div
              key={item.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div className="flex-1">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={item.status === "completed"}
                    onChange={() => {}}
                    className="h-4 w-4 text-[#202EC6] border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-900 dark:text-gray-100">
                    {item.task}
                  </span>
                  <span
                    className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusColor(item.status)}`}
                  >
                    {item.status}
                  </span>
                  <span
                    className={`ml-2 px-2 py-1 text-xs rounded-full ${getPriorityColor(item.priority)}`}
                  >
                    {item.priority}
                  </span>
                </div>
                <div className="mt-2">
                  <div className="text-sm text-gray-500 dark:text-gray-500">
                    Due: {formatDate(item.dueDate)}
                  </div>
                  <div className="mt-2 h-2 bg-gray-200 rounded-full">
                    <div
                      className="h-2 bg-[#202EC6] rounded-full"
                      style={{ width: `${item.progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render document repository
  const renderDocumentRepository = () => (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Document Repository
        </h2>
        <button className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90">
          Upload Document
        </button>
      </div>
      <div className="p-6">
        <div className="mb-4">
          <input
            type="text"
            placeholder="Search documents..."
            className="w-full p-2 border rounded-lg"
          />
        </div>
        <div className="space-y-4">
          {documents.map((doc) => (
            <div
              key={doc.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {doc.name}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500">
                  Uploaded by {doc.uploader} on {formatDate(doc.uploadDate)}
                </div>
                <div className="mt-1 flex space-x-2">
                  {doc.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 text-xs bg-gray-100 text-gray-800 dark:text-gray-200 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <button className="text-[#202EC6] hover:text-opacity-80">
                Download
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render audit logs
  const renderAuditLogs = () => (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Audit Logs</h2>
        <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
          Export Logs
        </button>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {auditLogs.map((log) => (
            <div
              key={log.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {log.action}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500">
                  {log.user} - {new Date(log.timestamp).toLocaleString()}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500">{log.details}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render tasks management
  const renderTasksManagement = () => (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Compliance Tasks</h2>
        <button className="px-4 py-2 bg-[#202EC6] text-white rounded-lg hover:bg-opacity-90">
          Assign Task
        </button>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {tasks.map((task) => (
            <div
              key={task.id}
              className="flex items-center justify-between p-4 border rounded-lg"
            >
              <div>
                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {task.name}
                </div>
                <div className="text-sm text-gray-500 dark:text-gray-500">
                  Assigned to: {task.assignee} | Due: {formatDate(task.dueDate)}
                </div>
                <div className="mt-1 flex space-x-2">
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getPriorityColor(task.priority)}`}
                  >
                    {task.priority}
                  </span>
                  <span
                    className={`px-2 py-1 text-xs rounded-full ${getStatusColor(task.status)}`}
                  >
                    {task.status}
                  </span>
                </div>
              </div>
              <button className="text-[#202EC6] hover:text-opacity-80">
                View Details
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // Render alerts & notifications
  const renderAlerts = () => (
    <div className="bg-surface-default dark:bg-surface-default rounded-lg shadow">
      <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">
          Alerts & Notifications
        </h2>
      </div>
      <div className="p-6">
        <div className="space-y-4">
          {alerts.map((alert) => (
            <div
              key={alert.id}
              className={`p-4 rounded-lg ${
                alert.type === "warning" ? "bg-yellow-50" : "bg-blue-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                    {alert.message}
                  </div>
                  <div className="text-sm text-gray-500 dark:text-gray-500">
                    {new Date(alert.timestamp).toLocaleString()}
                  </div>
                </div>
                {!alert.isRead && (
                  <span className="px-2 py-1 text-xs bg-red-100 text-red-800 rounded-full">
                    New
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <SubPageLayout
      title="Compliance Center"
      subtitle="Monitor and manage regulatory compliance requirements"
      breadcrumbItems={breadcrumbItems}
    >
      <div className="space-y-6">
        {/* Navigation Tabs */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="-mb-px flex space-x-8">
            {[
              "dashboard",
              "checklist",
              "documents",
              "audit",
              "tasks",
              "alerts",
            ].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`${
                  activeTab === tab
                    ? "border-[#202EC6] text-[#202EC6]"
                    : "border-transparent text-gray-500 dark:text-gray-500 hover:text-gray-700 dark:text-gray-300 hover:border-gray-300"
                } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === "dashboard" && (
          <>
            {renderDashboardWidgets()}
            {renderRegulatoryChecklist()}
          </>
        )}
        {activeTab === "checklist" && renderRegulatoryChecklist()}
        {activeTab === "documents" && renderDocumentRepository()}
        {activeTab === "audit" && renderAuditLogs()}
        {activeTab === "tasks" && renderTasksManagement()}
        {activeTab === "alerts" && renderAlerts()}
      </div>
    </SubPageLayout>
  );
};

export default ComplianceCenter;
